<template>
    <div 
        class="logo-upload"
        @dragover.prevent
        @drop.prevent="handleDrop"
    >
        <img 
            :src="currentLogoUrl"
            @error="handleImageError"
            class="company-logo"
            alt="Company logo"
            referrerpolicy="no-referrer"
        />
        <div class="drop-zone" :class="{ 'drag-over': isDragging }">
            <p>Drag and drop your logo here or</p>
            <div class="logo-buttons">
                <button 
                    @click.prevent="triggerFileInput" 
                    type="button" 
                    class="change-logo-btn"
                >
                    Choose File
                </button>
                <button 
                    v-if="currentLogoUrl !== DEFAULT_LOGO" 
                    type="button"
                    @click="removeLogo" 
                    class="remove-logo-btn"
                >
                    Remove Logo
                </button>
            </div>
        </div>
        <input
            type="file"
            ref="fileInput"
            @change="handleFileSelect"
            accept="image/*"
            style="display: none"
        />
    </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { getStorage, ref as storageRef, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { DEFAULT_LOGO } from '@/utils/photoUtils';
import { userService } from '@/services/api';

export default {
    name: 'CompanyLogoUpload',
    props: {
        initialLogoUrl: {
            type: String,
            required: false,
            default: ''
        },
        signedInUserId: {
            type: String,
            required: true
        },
        targetUserId: {
            type: String,
            required: false,
            default: null
        },
        isAdmin: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:logo', 'error'],
    setup(props, { emit }) {
        const currentLogoUrl = ref(props.initialLogoUrl);
        const fileInput = ref(null);
        const isDragging = ref(false);

        // Only warn if required props are missing
        if (!props.signedInUserId) {
            console.error('CompanyLogoUpload: signedInUserId is required');
        }
        if (!props.targetUserId) {
            console.error('CompanyLogoUpload: targetUserId is required');
        }

        const triggerFileInput = () => {
            // Prevent any default form submission
            event?.preventDefault();
            
            // Add a small delay to ensure event propagation is complete
            setTimeout(() => {
                fileInput.value.click();
            }, 0);
        };

        const handleDrop = (event) => {
            const file = event.dataTransfer?.files[0];
            if (file) {
                handleFile(file);
            }
            isDragging.value = false;
        };
        
        const handleFile = async (file) => {
            // Determine which user ID to use for the upload
            const uploadUserId = props.targetUserId || props.signedInUserId;

            // Validate file size (max 2MB)
            const MAX_SIZE = 2 * 1024 * 1024; // 2MB
            if (file.size > MAX_SIZE) {
                emit('error', new Error('File size must be less than 2MB'));
                return;
            }

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                emit('error', new Error('File must be JPEG, PNG, or GIF'));
                return;
            }

            try {
                const storage = getStorage();
                
                const storagePath = `company-logos/${uploadUserId}/${Date.now()}-${file.name}`;
                const fileRef = storageRef(storage, storagePath);
                
                const uploadTask = uploadBytesResumable(fileRef, file);
                
                uploadTask.on('state_changed',
                    () => {
                        // Progress tracking removed
                    },
                    (error) => {
                        console.error('Upload failed:', error);
                        emit('error', error);
                    },
                    async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        
                        try {
                            await userService.updateCompanyLogo(
                                downloadURL,
                                uploadUserId,
                                props.isAdmin
                            );
                            currentLogoUrl.value = downloadURL;
                            emit('update:logo', downloadURL);
                        } catch (err) {
                            console.error('Database update failed:', err);
                            emit('error', err);
                        }
                    }
                );
            } catch (error) {
                console.error('Critical error:', error);
                emit('error', error);
            }
        };

        const handleFileSelect = (event) => {
            const file = event.target.files[0];
            if (file) {
                handleFile(file);
            }
        };

        const removeLogo = async () => {
            try {
                await userService.updateCompanyLogo(props.signedInUserId, null);
                currentLogoUrl.value = DEFAULT_LOGO;
                emit('update:logo', DEFAULT_LOGO);
            } catch (err) {
                console.error('Error removing logo:', err);
                emit('error', err);
            }
        };

        const handleImageError = () => {
            currentLogoUrl.value = DEFAULT_LOGO;
            emit('update:logo', DEFAULT_LOGO);
        };

        onMounted(() => {
            currentLogoUrl.value = props.initialLogoUrl && props.initialLogoUrl !== 'null' 
                ? props.initialLogoUrl 
                : DEFAULT_LOGO;
        });

        watch(() => props.initialLogoUrl, (newUrl) => {
            if (newUrl) {
                currentLogoUrl.value = newUrl;
            } else {
                currentLogoUrl.value = DEFAULT_LOGO;
            }
        });

        return {
            currentLogoUrl,
            DEFAULT_LOGO,
            handleFileSelect,
            triggerFileInput,
            fileInput,
            removeLogo,
            handleImageError,
            handleDrop,
            isDragging
        };
    }
};
</script>

<style scoped>
.logo-upload {
    text-align: center;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    position: relative;
}

.company-logo {
    width: 200px;
    height: 100px;
    object-fit: contain;
    border: 2px solid #eee;
    border-radius: 4px;
    padding: 10px;
    background: white;
}

.drop-zone {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.9);
    border: 2px dashed #ccc;
    border-radius: 4px;
    opacity: 0;
    transition: opacity 0.2s, border-color 0.2s;
}

.logo-upload:hover .drop-zone,
.drop-zone.drag-over {
    opacity: 1;
}

.drop-zone.drag-over {
    border-color: #4285f4;
    background: rgba(66, 133, 244, 0.1);
}

.drop-zone p {
    margin: 0 0 10px 0;
    color: #666;
}

.logo-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
}

.change-logo-btn, .remove-logo-btn {
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    border: none;
    font-size: 14px;
    transition: background-color 0.2s;
}

.change-logo-btn {
    background-color: #4285f4;
    color: white;
}

.remove-logo-btn {
    background-color: #dc3545;
    color: white;
}

.change-logo-btn:hover {
    background-color: #3367d6;
}

.remove-logo-btn:hover {
    background-color: #c82333;
}
</style> 